import { create } from 'zustand';
import type { CurrentUser, LoginCredentials } from '@/shared/types/index';
import { authService } from '@/shared/api/auth.service';
import { queryClient } from '@/shared/providers/queryClient';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: CurrentUser | null;
  /** Restore the session on app boot via GET /api/auth/me. */
  bootstrap: () => Promise<void>;
  /** POST /api/auth/login then load the full profile via /me. */
  login: (credentials: LoginCredentials) => Promise<void>;
  /** POST /api/auth/logout and clear local state. */
  logout: () => Promise<void>;
  /** Local-only clear (used by the 401 interceptor). */
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  user: null,

  async bootstrap() {
    set({ status: 'loading' });
    try {
      const user = await authService.me();
      set({ status: 'authenticated', user });
    } catch {
      set({ status: 'unauthenticated', user: null });
    }
  },

  async login(credentials) {
    // login() sets the cookie; /me returns the full profile (incl. department).
    await authService.login(credentials);
    const user = await authService.me();
    // EVERY CACHED QUERY BELONGS TO WHOEVER FETCHED IT. React Query keys are
    // plain strings like ['mailbox', 'messages'] — none of them carry a user
    // id — so nothing distinguishes "BDM 1's inbox" from "BDM 2's inbox" in
    // the cache. Session cookies swap instantly on login/logout in this SPA
    // (no page reload), so without this, signing in as a second person in the
    // same tab within staleTime/gcTime showed the FIRST person's cached mail,
    // sales figures, anything per-user — this is how one BDM saw another
    // BDM's mailbox after each set up their own. Cleared on the way IN, not
    // only on the way out, so a session that expired and was silently
    // re-logged-in as someone else is covered too, not just an explicit
    // Logout click.
    queryClient.clear();
    set({ status: 'authenticated', user });
  },

  async logout() {
    try {
      await authService.logout();
    } finally {
      // Same reasoning as login() above — the outgoing identity's cached
      // data must not survive into whoever uses this tab next.
      queryClient.clear();
      set({ status: 'unauthenticated', user: null });
    }
  },

  clearSession() {
    // Reached by the 401 interceptor (src/shared/api/axios.ts) when the
    // server no longer recognises this session — same identity boundary as
    // logout(), so the same cache-clear applies.
    queryClient.clear();
    set({ status: 'unauthenticated', user: null });
  },
}));
